<template>
  <div
    class="flex w-full flex-col gap-y-4 px-6 py-4"
    data-testid="rx-step-lenstype"
  >
    <SupportHeadline
      v-if="isOverlayAvailable"
      class="my-4"
      :text="linkText"
      data-testid="rx-lenstype-overlay"
      @click="showOverlay"
    />
    <template v-for="lens in lensTypes" :key="lens.title">
      <RxSelectOption
        :icon="lens?.icon"
        :title="lens.title"
        :description="lens.description"
        :selected="selectedOption === lens.referenceKey"
        :data-testid="`rx-lenstype-option-${lens.referenceKey}`"
        @click:select="selectOption(lens)"
      >
        <div v-if="isProgressiveGlasses(lens.sortingKey)" class="px-6 pb-4">
          <FimRadio
            v-for="type in rxProgressiveGlassesPrescriptionConversionType"
            :key="type"
            v-model="conversionType"
            :data-testid="`rx-lenstype-conversion-${type}`"
            :radio-value="type"
            :radio-name="type"
            :value="type"
            :label="
              $t('rx.prescription.loadFromAccount.progressiveGlasses') +
              ' → ' +
              $t(`rx.prescription.loadFromAccount.${type}Glasses`)
            "
          />
        </div>
      </RxSelectOption>
    </template>
  </div>

  <Overlay
    v-if="isOverlayAvailable"
    :title="overlayTitle"
    :open="isOverlayOpen"
    layout="right"
    enter-from="right"
    @update:open="isOverlayOpen = false"
  >
    <div v-for="content in overlayContent" :key="content._uid">
      <component :is="content.component" :blok="content" />
    </div>
  </Overlay>
</template>

<script setup lang="ts">
const emit = defineEmits(['step:select-option', 'ui:update'])

const { lensTypes, setLensType, state, setPrescriptionValues, setIsHomeTryOn } =
  await useRxConfiguratorState()
const { data: prescriptionFromOrder } = usePrescriptionValuesFromOrder()

const isProgressiveGlasses = (sortingKey: number) =>
  sortingKey === 1 &&
  prescriptionFromOrder.value.isConvertibleProgressiveGlasses

const conversionType = ref<RxProgressiveGlassesPrescriptionConversionType>(
  prescriptionFromOrder.value.prescriptionValues?.type || 'distance',
)

const scope = effectScope()
onScopeDispose(() => scope.stop())

scope.run(() => {
  if (import.meta.server) {
    return
  }

  watch(conversionType, (updatedConversionType) => {
    if (prescriptionFromOrder.value.rawPrescription) {
      const payload = {
        type: conversionType.value,
        ...convertProgressiveGlasses({
          prescription: prescriptionFromOrder.value.rawPrescription,
          to: updatedConversionType,
        }),
      }

      // update session
      prescriptionFromOrder.value.prescriptionValues = payload

      // update RX configurator
      setPrescriptionValues(formatPrescriptionToRxConfigurator(payload))
    }
  })
})

const selectedOption = ref<SingleOption['referenceKey'] | null>(
  state.value?.lensType?.variant?.referenceKey || null,
)

const selectOption = (lensType: SingleOption) => {
  selectedOption.value = lensType.referenceKey
  const hasPrescription = lensType.referenceKey === LensTypesRefKeys.EI

  setIsHomeTryOn(lensType.referenceKey === LensTypesRefKeys.HOME_TRY_ON)

  if ('variantId' in lensType) {
    setLensType(lensType.variantId)
  }

  emit('step:select-option', { hasPrescription })
  updateMainAction()
}

const updateMainAction = () => {
  let mainAction: RxMainAction = 'continue'

  switch (selectedOption.value) {
    case LensTypesRefKeys.APPOINTMENT:
      mainAction = 'appointment'
      break
    case LensTypesRefKeys.HOME_TRY_ON:
      mainAction = 'addToBasket'
      break
    default:
      mainAction = 'continue'
  }

  emit('ui:update', {
    mainAction,
  })
}

onMounted(() => {
  if (selectedOption.value) {
    updateMainAction()
  } else {
    // select first option as default
    selectOption(lensTypes.value[0])
  }
})

const {
  overlayTitle,
  overlayContent,
  linkText,
  open: isOverlayOpen,
  show: showOverlay,
  available: isOverlayAvailable,
} = await useOverlay(RxOverlays.LENS_TYPES)
</script>
